import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import SEO from "../components/seo/SEO";

const plans = [
  { age: "Ages 0-22", price: 60, popular: false },
  { age: "Ages 23-39", price: 110, popular: true },
  { age: "Ages 40-49", price: 160, popular: false },
  { age: "Ages 50+", price: 210, popular: false }
];

const included = [
  "Unlimited Doctor Consultations",
  "24/7 Virtual Access",
  "Personalized Care Plans",
  "Daily Health Monitoring",
  "Priority Appointments",
  "Discounted Lab Tests",
  "Prescription Delivery",
  "Family Health Dashboard"
];

export default function Pricing() {
  return (
    <>
      <SEO
        title="Membership Pricing"
        description="Affordable concierge healthcare memberships starting ₹60/month. Unlimited doctor access, 24/7 care, no insurance hassles."
        canonical="/pricing"
      />
      <Box component="main" sx={{ pt: "96px" }}>
        <Box component="section" className="section-padding wellness-bg">
          <Box className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ textAlign: "center", maxWidth: 768, margin: "0 auto 64px" }}
            >
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  fontWeight: 800,
                  mb: 3,
                  lineHeight: 1.1
                }}
              >
                Simple, Transparent
                <Box component="span" className="gradient-text" sx={{ display: "block" }}>
                  Pricing
                </Box>
              </Typography>
              <Typography sx={{ fontSize: "1.125rem", color: "text.secondary" }}>
                Direct Primary Care without insurance middlemen. Unlimited access to your personal
                doctor at one flat monthly fee.
              </Typography>
            </motion.div>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 3, mb: 8 }}>
              {plans.map((plan, i) => (
                <motion.div
                  key={plan.age}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Box
                    className="premium-card"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      position: "relative",
                      outline: plan.popular ? "2px solid #27a599" : "none"
                    }}
                  >
                    {plan.popular && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -12,
                          left: "50%",
                          transform: "translateX(-50%)",
                          px: 1.5,
                          py: 0.5,
                          bgcolor: "primary.main",
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: 999
                        }}
                      >
                        Most Popular
                      </Box>
                    )}

                    <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 1 }}>{plan.age}</Typography>
                    <Typography sx={{ fontSize: "2.25rem", fontWeight: 800, mb: 0.5 }}>
                      ${plan.price}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 3 }}>/month</Typography>

                    <Button
                      component={Link}
                      to="/signup"
                      fullWidth
                      variant={plan.popular ? "contained" : "outlined"}
                      sx={
                        plan.popular
                          ? { bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "rgba(39,165,153,0.9)" } }
                          : { borderColor: "hsla(200, 20%, 90%, 1)" }
                      }
                    >
                      Get Started
                    </Button>
                  </Box>
                </motion.div>
              ))}
            </Box>

            <Box className="glass-card" sx={{ p: 4, maxWidth: 672, mx: "auto" }}>
              <Typography sx={{ fontSize: 20, fontWeight: 800, mb: 3, textAlign: "center" }}>
                What's Included
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                {included.map((item) => (
                  <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CheckRoundedIcon sx={{ color: "primary.main" }} />
                    <span>{item}</span>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mt: 6 }}>
              <Box className="premium-card" sx={{ p: 3, display: "flex", alignItems: "center", gap: 2 }}>
                <GroupsRoundedIcon sx={{ fontSize: 40, color: "primary.main" }} />
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>Family Plans</Typography>
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                    Save 20% on family memberships
                  </Typography>
                </Box>
              </Box>

              <Box className="premium-card" sx={{ p: 3, display: "flex", alignItems: "center", gap: 2 }}>
                <BusinessRoundedIcon sx={{ fontSize: 40, color: "#39ac82" }} />
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>Corporate Plans</Typography>
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                    Custom wellness for your team
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
